import { activeFilters, type ReportFilterRow } from './reportFilters';

/**
 * Applies the toolbar's search + filter rows to an ALREADY-FETCHED list.
 *
 * Deliberately the client-side twin of the API's `report-filter.ts`, and
 * the two are used in different places for a reason:
 *
 *  - `sales-history` is server-paginated (25 rows at a time), so its
 *    predicate MUST run in the query — narrowing the fetched page would
 *    only ever search the rows on screen, and `total` would still report
 *    the unfiltered count.
 *  - Every other report endpoint returns its full result set, which is
 *    already in the browser. Filtering it here is exactly equivalent, and
 *    avoids threading a predicate through hand-written SQL that has no
 *    pagination to get wrong.
 *
 * Semantics match the server: case-insensitive, search ORs across its
 * columns, filter rows OR/AND per [matchAny], and search composes with
 * filters by AND (search narrows, it does not widen).
 */

export interface ClientFilterOptions<T> {
  search: string;
  filterRows: readonly ReportFilterRow[];
  matchAny: boolean;
  /** Field name (as shown in the popover) → that row's value for it. */
  fieldValue: (row: T, field: string) => string | undefined;
  /** Values the free-text search looks across. */
  searchValues: (row: T) => ReadonlyArray<string | undefined>;
}

function matches(value: string, condition: ReportFilterRow['condition'], term: string): boolean {
  const a = value.toLowerCase();
  const b = term.trim().toLowerCase();
  switch (condition) {
    case 'equals':
      return a === b;
    case 'starts with':
      return a.startsWith(b);
    case 'ends with':
      return a.endsWith(b);
    case 'contains':
    default:
      return a.includes(b);
  }
}

export function applyClientFilters<T>(rows: readonly T[], options: ClientFilterOptions<T>): T[] {
  const term = options.search.trim().toLowerCase();
  const active = activeFilters(options.filterRows);

  return rows.filter((row) => {
    if (term !== '') {
      const hit = options
        .searchValues(row)
        .some((value) => (value ?? '').toLowerCase().includes(term));
      if (!hit) return false;
    }
    if (active.length === 0) return true;

    const test = (filter: ReportFilterRow) => {
      // A field this page doesn't expose constrains nothing, mirroring the
      // server dropping a column it never offered.
      const value = options.fieldValue(row, filter.field);
      return value === undefined ? false : matches(value, filter.condition, filter.value);
    };
    return options.matchAny ? active.some(test) : active.every(test);
  });
}
